import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api, useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import { PenLine, Loader2, Check, X, Trophy, RotateCcw, ArrowRight, ArrowLeft, Lock } from 'lucide-react';
import { toast } from 'sonner';

const StudentFTBPage = () => {
  const { user } = useAuth();
  const [tests, setTests] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({}); // { questionId: [answer1, answer2, ...] }
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(null);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const res = await api.get(`/tests?module_type=ftb`);
      setTests(res.data);
    } catch (err) {
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (testId) => {
    try {
      const res = await api.get(`/tests/${testId}`);
      const testData = res.data;
      
      // Process questions - parse correct_answers and options
      const processedQuestions = testData.questions.map(q => {
        const correctAnswers = Array.isArray(q.correct_answers) ? q.correct_answers : 
                              (typeof q.correct_answers === 'string' ? JSON.parse(q.correct_answers) : []);
        const wordBank = Array.isArray(q.options) ? q.options : 
                        (typeof q.options === 'string' ? JSON.parse(q.options) : null);
        
        // Count blanks in question text
        const blankCount = (q.question_text.match(/___+/g) || []).length;
        
        return { 
          ...q, 
          correct_answers: correctAnswers,
          blank_count: blankCount
        };
      });
      
      setQuestions(processedQuestions);
      setSelectedTestId(testId);
      setCurrentIndex(0);
      
      // Initialize answers with empty arrays
      const initialAnswers = {};
      processedQuestions.forEach(q => {
        initialAnswers[q.id] = Array(q.blank_count).fill('');
      });
      setAnswers(initialAnswers);
      
      setSubmitted(false);
      setResult(null);
    } catch (err) {
      toast.error('Failed to load test');
    }
  };

  const updateAnswer = (questionId, blankIndex, value) => {
    if (submitted) return;
    setAnswers(prev => {
      const questionAnswers = [...(prev[questionId] || [])];
      questionAnswers[blankIndex] = value;
      return { ...prev, [questionId]: questionAnswers };
    });
  };

  const handleSubmit = async () => {
    // Check if all blanks are filled
    let allFilled = true;
    questions.forEach(q => {
      const qAnswers = answers[q.id] || [];
      for (let i = 0; i < q.blank_count; i++) {
        if (!qAnswers[i] || qAnswers[i].trim() === '') {
          allFilled = false;
          break;
        }
      }
    });

    if (!allFilled) {
      toast.error('Please fill in all blanks before submitting');
      return;
    }

    setSubmitting(true);
    try {
      const formattedAnswers = questions.map(q => ({
        question_id: q.id,
        answers: answers[q.id] || []
      }));

      const res = await api.post('/ftb/submit', formattedAnswers);
      setResult(res.data);
      setSubmitted(true);
    } catch (err) {
      toast.error('Failed to submit test');
    } finally {
      setSubmitting(false);
    }
  };

  const resetQuiz = () => {
    const initialAnswers = {};
    questions.forEach(q => {
      initialAnswers[q.id] = Array(q.blank_count).fill('');
    });
    setAnswers(initialAnswers);
    setSubmitted(false);
    setResult(null);
    setCurrentIndex(0);
  };

  // Render question text with input fields for blanks
  const renderQuestionWithBlanks = (question) => {
    const parts = question.question_text.split(/(___+)/g);
    let blankIndex = 0;
    const questionAnswers = answers[question.id] || [];
    
    return (
      <div className="text-xl leading-relaxed flex flex-wrap items-center gap-1">
        {parts.map((part, idx) => {
          if (part.match(/___+/)) {
            const currentBlankIndex = blankIndex++;
            const currentAnswer = questionAnswers[currentBlankIndex] || '';
            
            // In result mode, show correct/incorrect styling
            if (submitted && result) {
              const questionResult = result.results.find(r => r.question_id === question.id);
              const blankResult = questionResult?.blanks?.[currentBlankIndex];
              const isCorrect = blankResult?.is_correct;
              
              return (
                <span key={idx} className="inline-flex flex-col items-center mx-1">
                  <span className={`px-3 py-1 rounded-lg font-semibold ${
                    isCorrect 
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-2 border-emerald-500' 
                      : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300 border-2 border-rose-500'
                  }`}>
                    {currentAnswer || '—'}
                  </span>
                  {!isCorrect && (
                    <span className="text-xs text-emerald-600 mt-1">
                      ✓ {blankResult?.correct_answer}
                    </span>
                  )}
                </span>
              );
            }
            
            // Input mode
            return (
              <Input
                key={idx}
                type="text"
                value={currentAnswer}
                onChange={(e) => updateAnswer(question.id, currentBlankIndex, e.target.value)}
                placeholder={`Blank ${currentBlankIndex + 1}`}
                className="w-32 inline-block mx-1 text-center font-medium border-2 border-orange-300 focus:border-orange-500"
                disabled={submitted}
              />
            );
          }
          return <span key={idx} className="text-slate-900 dark:text-white">{part}</span>;
        })}
      </div>
    );
  };

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  
  // Count filled blanks
  const totalBlanks = questions.reduce((sum, q) => sum + q.blank_count, 0);
  const filledBlanks = Object.entries(answers).reduce((sum, [qId, ans]) => {
    return sum + (ans || []).filter(a => a && a.trim() !== '').length;
  }, 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Show test cards if no test is selected
  if (!selectedTestId) {
    if (tests.length === 0) {
      return (
        <DashboardLayout>
          <div className="max-w-2xl mx-auto text-center py-12">
            <PenLine className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              No Fill the Blanks Tests Available
            </h2>
            <p className="text-slate-500">
              Your teacher hasn't assigned any fill-the-blanks tests yet.
            </p>
          </div>
        </DashboardLayout>
      );
    }

    return (
      <DashboardLayout>
        <div className="space-y-6 animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              Fill the Blanks Tests
            </h1>
            <p className="text-slate-500">
              Complete sentences by filling in the missing words
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tests.map((test) => {
              const isLocked = !!test.is_locked;
              return (
              <Card key={test.id} className={`module-card h-full group transition-shadow ${isLocked ? 'opacity-75' : 'cursor-pointer hover:shadow-lg'}`} onClick={() => !isLocked && fetchQuestions(test.id)}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                      {test.test_number}
                    </span>
                    {isLocked && (
                      <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-700">
                        🔒 Locked
                      </span>
                    )}
                  </div>
                  <div className="inline-flex p-3 rounded-xl bg-sky-100 dark:bg-sky-900/30 mb-3 group-hover:scale-110 transition-transform">
                    <PenLine className="h-6 w-6 text-sky-600 dark:text-sky-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                    {test.test_name}
                  </h3>
                  <p className="text-xs text-slate-500 mb-3">{test.question_count || 0} sentences</p>
                  {isLocked ? (
                    <p className="text-xs text-amber-600 mt-2">This test is locked. You cannot attempt it.</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-sm mt-2">
                        <span className="text-slate-500">Attempts: {test.attempts || 0}</span>
                        <span className={`font-semibold ${(test.best_score || 0) >= 80 ? 'text-emerald-600' : (test.best_score || 0) >= 50 ? 'text-amber-600' : 'text-slate-600'}`}>
                          Best: {test.best_score || 0}%
                        </span>
                      </div>
                      <div className="mt-4 flex items-center text-sky-600 dark:text-sky-400 text-sm font-medium group-hover:translate-x-1 transition-transform">
                        Start Test <ArrowRight className="h-4 w-4 ml-1" />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (questions.length === 0) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <PenLine className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            No Questions Available
          </h2>
          <p className="text-slate-500">
            Failed to load questions for this test.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  // Show result screen
  if (submitted && result) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
          <Card className="overflow-hidden">
            <div className={`p-8 text-center ${
              result.percentage >= 80 ? 'bg-emerald-500' :
              result.percentage >= 50 ? 'bg-amber-500' :
              'bg-rose-500'
            }`}>
              <Trophy className="h-16 w-16 mx-auto text-white mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">Test Complete!</h2>
              <p className="text-white/90 text-lg">
                You got {result.correct_blanks} out of {result.total_blanks} blanks correct
              </p>
            </div>
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <p className="text-5xl font-bold text-slate-900 dark:text-white mb-2">
                  {result.percentage}%
                </p>
                <p className={`text-xl ${
                  result.percentage >= 80 ? 'text-emerald-600' :
                  result.percentage >= 50 ? 'text-amber-600' :
                  'text-rose-600'
                }`}>
                  {result.percentage >= 80 ? 'Excellent!' :
                   result.percentage >= 50 ? 'Good job!' :
                   'Keep practicing!'}
                </p>
              </div>

              {/* Results breakdown */}
              <div className="space-y-4 mb-8">
                <h3 className="font-semibold text-slate-900 dark:text-white">Review Answers:</h3>
                {result.results.map((qResult, idx) => {
                  const question = questions.find(q => q.id === qResult.question_id);
                  const allCorrect = qResult.blanks.every(b => b.is_correct);
                  
                  return (
                    <div 
                      key={qResult.question_id} 
                      className={`p-4 rounded-xl border-2 ${
                        allCorrect ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20' :
                        'border-rose-200 bg-rose-50 dark:bg-rose-900/20'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-1 rounded-full ${allCorrect ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                          {allCorrect ? (
                            <Check className="h-4 w-4 text-white" />
                          ) : (
                            <X className="h-4 w-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 dark:text-white mb-3">
                            {idx + 1}. {question ? renderQuestionWithBlanks(question) : qResult.question_text}
                          </p>
                          <p className="text-sm text-slate-600 dark:text-slate-300">
                            Score: {qResult.blanks_correct}/{qResult.blanks_total} blanks correct ({Math.round(qResult.score)}%)
                          </p>
                          {qResult.explanation && (
                            <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                              <p className="text-xs text-blue-700 dark:text-blue-300">
                                <span className="font-semibold">Explanation: </span>{qResult.explanation}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-center gap-4">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSelectedTestId(null);
                    setQuestions([]);
                    setAnswers({});
                    setSubmitted(false);
                    setResult(null);
                  }}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Tests
                </Button>
                <Button onClick={resetQuiz} className="bg-orange-600 hover:bg-orange-700" data-testid="retry-quiz-btn">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedTestId(null);
              setQuestions([]);
              setCurrentIndex(0);
              setAnswers({});
              setSubmitted(false);
              setResult(null);
            }}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Tests
          </Button>
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Fill the Blanks
          </h1>
          <p className="text-slate-500">
            Fill in all blanks and submit to see your score
          </p>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-slate-500">
            <span>Question {currentIndex + 1} of {questions.length}</span>
            <span>{filledBlanks}/{totalBlanks} blanks filled</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card>
          <CardContent className="p-8">
            <div className="mb-6">
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300">
                Question {currentIndex + 1}
              </span>
            </div>
            
            <div className="mb-8">
              {renderQuestionWithBlanks(currentQuestion)}
            </div>

          </CardContent>
        </Card>

        {/* Navigation & Submit */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setCurrentIndex(prev => prev - 1)}
              disabled={currentIndex === 0}
              data-testid="prev-question-btn"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => setCurrentIndex(prev => prev + 1)}
              disabled={currentIndex === questions.length - 1}
              data-testid="next-question-btn"
            >
              Next
            </Button>
          </div>
          
          {filledBlanks === totalBlanks && (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-quiz-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Submit Test
            </Button>
          )}
        </div>

        {/* Question Navigation Pills */}
        <div className="flex flex-wrap gap-2 justify-center">
          {questions.map((q, idx) => {
            const qAnswers = answers[q.id] || [];
            const isComplete = qAnswers.length === q.blank_count && qAnswers.every(a => a && a.trim() !== '');
            const isCurrent = idx === currentIndex;
            return (
              <button
                key={q.id}
                onClick={() => setCurrentIndex(idx)}
                className={`w-10 h-10 rounded-full font-medium transition-all ${
                  isCurrent 
                    ? 'bg-orange-600 text-white' 
                    : isComplete 
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
                      : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 hover:bg-slate-200'
                }`}
                data-testid={`question-nav-${idx}`}
              >
                {idx + 1}
              </button>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentFTBPage;
